'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { InquiryFlowSnapshot } from '@miso/design-system';
import type {
  RecordDepartureSiteRequest,
  MovingSiteRequest,
} from '@/entities/visit-counsel/model/intake.types';
import {
  buildMovingMethodFlow,
  flowResultToMovingMethod,
  movingMethodToFlowValues,
} from '../model/buildMovingMethodFlow';
import { structuredToStartAddress } from '../model/addressAdapters';
import { createProgressFlowFetcher } from '@/shared/api/inquiry-flow/fetchProgressFlow';
import { ensureIntakeSessionStarted } from '../model/ensureIntakeSessionStarted';
import { findLastVisibleQuestionId } from '../model/findLastVisibleQuestionId';
import { StepFlow } from './StepFlow';

const FLOW_ID = 'progress-departure-transport';

interface Props {
  visitCounselId: string;
  counsel: VisitCounselView;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  onAfterDone: (counsel: VisitCounselView) => void;
  onCancel: () => void;
  /** 이전 sub-stage 로부터 뒤로가기 진입 — 마지막 visible 질문에 위치. */
  startAtLast?: boolean;
  progressOffset?: number;
  progressTotal?: number;
  onStateChange?: (snap: InquiryFlowSnapshot) => void;
}

/**
 * 출발지 운반방식 step (Figma `219:13240` ~ `222:14633`).
 *
 * 단일 InquiryFlow 안에 운반방식 single-select + 분기 sub-question 들 (사다리/스카이
 * 진입 위치, 계단 경사로/계단수, 엘리베이터 유무) 을 모두 포함. terminal 에서
 * `recordDepartureSite` 호출.
 *
 * 주의: `recordDepartureSite` 는 `MovingSiteRequest.supplyArea` 필수. 출발지 평수는
 * 상담 시작 시 이미 입력되어 `counsel.supplyArea` 또는 `counsel.sessions.intake.departure.supplyArea`
 * 에 보관되어 있다. 이 값을 그대로 재전송한다 (운반방식만 갱신).
 */
export function DepartureTransportStepAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
  startAtLast,
  progressOffset,
  progressTotal,
  onStateChange,
}: Props) {
  // Firestore `playground_flows/progress-departure-transport` 의 FlowDocument 를 비동기 로드.
  const fetcher = useMemo(() => createProgressFlowFetcher(FLOW_ID), []);

  // 뒤로가기 진입 위치 계산용 로컬 FlowDocument — Firestore 응답을 동기로 받을 수 없으므로
  // 같은 빌더를 그대로 사용해 `findLastVisibleQuestionId` 를 호출한다.
  // Firestore 버전이 빌더와 다르게 편집된 경우 back-nav 위치가 어긋날 수 있으나,
  // 본 PR 의 등록 스크립트가 같은 빌더로 직렬화하므로 이후 FlowEditor 에서 수동 편집
  // 하기 전까지는 정확히 일치한다. (follow-up: 빌더 deprecation 시 Firestore 응답을
  // 캐시해 사용하는 방식으로 전환 필요.)
  const localFlowForBackNav = useMemo(() => buildMovingMethodFlow({ site: 'departure' }), []);

  const initialValues = useMemo(
    () =>
      movingMethodToFlowValues(
        counsel.departureMovingMethod ?? null,
        counsel.departureHasElevator ?? null
      ),
    [counsel.departureMovingMethod, counsel.departureHasElevator]
  );

  // 뒤로가기 진입 — 저장된 운반방식 + 후속 답변 기준 마지막 visible 질문 위치.
  const startAtQuestionId = useMemo(() => {
    if (!startAtLast) return undefined;
    return findLastVisibleQuestionId(localFlowForBackNav, 'transport', initialValues ?? {});
  }, [startAtLast, localFlowForBackNav, initialValues]);

  // 마운트 시점 intake-session 보장.
  useEffect(() => {
    void ensureIntakeSessionStarted(visitCounselId, counsel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = useCallback(
    async (values: Record<string, unknown>) => {
      const result = flowResultToMovingMethod(values);
      if (!result) {
        showToast('운반방식을 선택해주세요.', 'warning');
        return;
      }

      // 상담 시작 시 입력된 출발지 평수/주소를 재전송 (백엔드는 전체 MovingSiteRequest 를 기대).
      const intakeDeparture = counsel.sessions?.intake?.departure;
      const depAddr = counsel.departureAddress ?? intakeDeparture?.address ?? null;
      const supplyArea =
        (typeof intakeDeparture?.supplyArea === 'number' && intakeDeparture.supplyArea > 0
          ? intakeDeparture.supplyArea
          : null) ??
        (typeof counsel.supplyArea === 'number' && counsel.supplyArea > 0
          ? counsel.supplyArea
          : null) ??
        1;

      const departure: MovingSiteRequest = {
        supplyArea,
        address: depAddr ? structuredToStartAddress(depAddr) : null,
        hasElevator: result.hasElevator,
        movingMethod: result.movingMethod,
      };
      const request: RecordDepartureSiteRequest = { departure };

      try {
        await ensureIntakeSessionStarted(visitCounselId, counsel);
        await myVisitCounselApi.recordDepartureSite(visitCounselId, request);
        showToast('출발지 운반방식이 저장되었습니다.', 'success');
        const refetched = await refetch();
        const updated = refetched?.counsel ?? null;
        if (updated) onAfterDone(updated);
        else onCancel();
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : '출발지 운반방식 저장에 실패했습니다.',
          'error'
        );
      }
    },
    [visitCounselId, counsel, showToast, refetch, onAfterDone, onCancel]
  );

  return (
    <StepFlow
      fetcher={fetcher}
      initialValues={initialValues}
      onComplete={handleComplete}
      onCancel={onCancel}
      onError={(err) => showToast(err.message ?? '플로우 오류가 발생했습니다.', 'error')}
      startAtQuestionId={startAtQuestionId}
      progressOffset={progressOffset}
      progressTotal={progressTotal}
      onStateChange={onStateChange}
    />
  );
}
